import { useInfiniteQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { fetchVerticalDocumentActivity } from 'services/verticalsApi'
import { ReviewItem } from '../types'
import { REVIEW_ACTIVITY_TYPE, buildListingFilters } from './reviewQueryUtils'
import type { ListingReviewQueryParams } from './reviewQueryUtils'

const SORT_PARAM = JSON.stringify([{ activityDate: 'desc' }])

interface UseListingReviewsQueryProps extends ListingReviewQueryParams {
  selectedPlatform?: string | null
  pageSize: number
}

export const useListingReviewsQuery = ({
  entityId,
  listingId,
  activityIndex,
  selectedPlatform,
  pageSize,
}: UseListingReviewsQueryProps) => {
  const { data, isLoading, isFetching, isError, fetchNextPage, hasNextPage } = useInfiniteQuery<{
    items: ReviewItem[]
    total: number
  }>({
    queryKey: ['str-listing-reviews', entityId, listingId, activityIndex, selectedPlatform, pageSize],
    queryFn: async ({ pageParam = 0 }) => {
      const fromIndex = pageParam * pageSize
      return await fetchVerticalDocumentActivity(
        entityId!,
        buildListingFilters(listingId!, selectedPlatform),
        fromIndex,
        activityIndex,
        null,
        pageSize,
        REVIEW_ACTIVITY_TYPE,
        SORT_PARAM,
      )
    },
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage?.items?.length) return
      const totalFetched = allPages.reduce((acc, page) => acc + (page?.items?.length ?? 0), 0)
      return totalFetched < (lastPage?.total ?? 0) ? allPages.length : undefined
    },
    enabled: !!entityId && !!listingId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  })

  const reviews = useMemo(() => data?.pages.flatMap(page => page?.items ?? []) ?? [], [data])

  const total = data?.pages?.[0]?.total ?? 0

  return {
    reviews,
    total,
    isLoading,
    isFetching,
    isError,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
  }
}
